import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../../utils/api';
import AttendanceRow from './AttendanceRow';
import './CheckoutProcessor.css';

const ATTENDEE_TYPES = [
    { attendeeTypeId: 1, name: 'NPC' },
    { attendeeTypeId: 2, name: 'PC' },
    { attendeeTypeId: 3, name: 'Plot' }
];
const PC_ATTENDEE_TYPE_ID = 2;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '—';
    const s = new Date(startDate + 'T00:00:00');
    const month = MONTHS[s.getMonth()];
    const dayStart = s.getDate();
    if (!endDate || endDate === startDate) return `${month} ${dayStart}`;
    const e = new Date(endDate + 'T00:00:00');
    if (s.getMonth() === e.getMonth()) return `${month} ${dayStart}–${e.getDate()}`;
    return `${month} ${dayStart} – ${MONTHS[e.getMonth()]} ${e.getDate()}`;
};

const CheckoutProcessor = ({ token, event }) => {
    api.setToken(token);

    const [scans, setScans] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedPlayerId, setSelectedPlayerId] = useState(null);
    const [scanDetail, setScanDetail] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [finalizing, setFinalizing] = useState(false);

    const [showFullImage, setShowFullImage] = useState(false);
    const [fullImageUrl, setFullImageUrl] = useState(null);
    const [loadingFullImage, setLoadingFullImage] = useState(false);

    const imageRef = useRef(null);
    const [snapshotMaxHeight, setSnapshotMaxHeight] = useState(null);

    const eventDays = useMemo(() => {
        if (!event.startDate || !event.endDate) return 1;
        const start = new Date(event.startDate + 'T00:00:00');
        const end = new Date(event.endDate + 'T00:00:00');
        return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
    }, [event]);

    const loadLists = async () => {
        try {
            setLoading(true);
            const [scanRows, attRows, playerRows] = await Promise.all([
                api.fetchCheckoutScansForEvent(event.eventId),
                api.fetchAttendance(event.eventId),
                api.fetchAllPlayers(),
            ]);
            setScans(scanRows || []);
            setAttendance(attRows || []);
            setPlayers((playerRows || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        } catch {
            toast.error('Failed to load checkout data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadLists(); }, [event.eventId]);

    const pcAttendees = useMemo(
        () => attendance.filter((a) => a.attendeeTypeId === PC_ATTENDEE_TYPE_ID),
        [attendance]
    );

    // Most recent character-sheet scan per player, for the "has scan" flag and image/snapshot lookup.
    const scanByPlayerId = useMemo(() => {
        const m = new Map();
        scans.forEach((s) => {
            const existing = m.get(s.playerId);
            if (!existing || new Date(s.uploadTimestamp) > new Date(existing.uploadTimestamp)) {
                m.set(s.playerId, s);
            }
        });
        return m;
    }, [scans]);

    const handleSelectAttendee = async (att) => {
        setSelectedPlayerId(att.playerId);
        setScanDetail(null);
        setImageUrl(null);
        setFullImageUrl(null);
        setSnapshotMaxHeight(null);

        const scan = scanByPlayerId.get(att.playerId);
        if (!scan) return;

        setLoadingDetail(true);
        try {
            const [detail, url] = await Promise.all([
                api.fetchCheckoutScanDetail(scan.eventScanId),
                api.fetchCheckoutScanImageUrl(scan.eventScanId),
            ]);
            setScanDetail(detail);
            setImageUrl(url);
        } catch {
            toast.error('Failed to load scan detail');
        } finally {
            setLoadingDetail(false);
        }
    };

    const selectedAttendee = pcAttendees.find((a) => a.playerId === selectedPlayerId) || null;
    const selectedScan = selectedPlayerId ? scanByPlayerId.get(selectedPlayerId) : null;

    useEffect(() => {
        const el = imageRef.current;
        if (!el) { setSnapshotMaxHeight(null); return undefined; }
        const update = () => setSnapshotMaxHeight(el.offsetHeight || null);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [imageUrl]);

    const handleSaveAttendee = async (playerId, data) => {
        try {
            await api.upsertAttendance(event.eventId, playerId, data);
            if (selectedScan && !selectedScan.processed) {
                await api.markCheckoutScanProcessed(selectedScan.eventScanId);
            }
            await loadLists();
            return true;
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to save checkout');
            return false;
        }
    };

    const handleToggleFinalize = async () => {
        if (!selectedAttendee) return;
        setFinalizing(true);
        try {
            await api.finalizeAttendance(event.eventId, selectedAttendee.playerId);
            toast.success(selectedAttendee.checkoutProcessed ? 'Checkout un-finalized' : 'Checkout finalized');
            await loadLists();
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to update finalize status');
        } finally {
            setFinalizing(false);
        }
    };

    const handleOpenFullImage = async () => {
        if (!selectedScan) return;
        setShowFullImage(true);
        if (fullImageUrl) return;
        setLoadingFullImage(true);
        try {
            const url = await api.fetchCheckoutScanFullImageUrl(selectedScan.eventScanId);
            setFullImageUrl(url);
        } catch {
            toast.error('Failed to load full-size image');
        } finally {
            setLoadingFullImage(false);
        }
    };

    if (loading) return <div className="em-loading">Loading checkout data…</div>;

    return (
        <div className="cp-container">
            <div className="em-attendance-header">
                <h2 className="em-subtitle">{event.site} — {formatDateRange(event.startDate, event.endDate)}</h2>
                <div className="em-attendance-stats">
                    {pcAttendees.length} PC attendee{pcAttendees.length !== 1 ? 's' : ''}
                </div>
            </div>

            {pcAttendees.length === 0 ? (
                <div className="em-empty">No PC attendees registered for this event yet.</div>
            ) : (
                <div className="cp-layout">
                    <ul className="cp-scan-list">
                        {pcAttendees.map((a) => {
                            const scan = scanByPlayerId.get(a.playerId);
                            return (
                                <li
                                    key={a.playerId}
                                    className={`cp-scan-item ${selectedPlayerId === a.playerId ? 'cp-selected' : ''}`}
                                    onClick={() => handleSelectAttendee(a)}
                                >
                                    <span className="cp-scan-character">{a.characterName || 'No character'}</span>
                                    <span className="cp-scan-player">{a.playerName}</span>
                                    <div className="cp-scan-badges">
                                        <span className={`cp-scan-badge ${scan ? 'cp-badge-processed' : 'cp-badge-pending'}`}>
                                            {scan ? 'Scanned' : 'No Scan'}
                                        </span>
                                        <span className={`cp-scan-badge ${a.checkoutProcessed ? 'cp-badge-finalized' : 'cp-badge-pending'}`}>
                                            {a.checkoutProcessed ? 'Finalized' : 'Pending'}
                                        </span>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>

                    <div className="cp-detail">
                        {!selectedPlayerId && <div className="em-empty">Select an attendee to process.</div>}

                        {selectedAttendee && (
                            <>
                                {loadingDetail && <div className="em-loading">Loading…</div>}

                                {!loadingDetail && (
                                    <>
                                        {scanDetail ? (
                                            <div className="cp-detail-top">
                                                {imageUrl && (
                                                    <img
                                                        ref={imageRef}
                                                        src={imageUrl}
                                                        alt="Scanned character sheet"
                                                        className="cp-scan-image"
                                                        onClick={handleOpenFullImage}
                                                        onLoad={() => setSnapshotMaxHeight(imageRef.current?.offsetHeight || null)}
                                                    />
                                                )}
                                                <div
                                                    className="cp-snapshot"
                                                    style={snapshotMaxHeight ? { maxHeight: snapshotMaxHeight } : undefined}
                                                >
                                                    <div className="cp-snapshot-title">Captured Build Snapshot</div>
                                                    <div><strong>Name:</strong> {scanDetail.snapshot?.name}</div>
                                                    <div><strong>Race:</strong> {scanDetail.snapshot?.raceName} {scanDetail.snapshot?.subRace}</div>
                                                    <div><strong>Skillsets:</strong> {scanDetail.snapshot?.skillsetPrimaryName}{scanDetail.snapshot?.skillsetSecondaryName ? ` / ${scanDetail.snapshot.skillsetSecondaryName}` : ''}</div>
                                                    {scanDetail.snapshot?.transformName && <div><strong>Transform:</strong> {scanDetail.snapshot.transformName}</div>}
                                                    <div><strong>SP:</strong> {scanDetail.snapshot?.skillPointsTotal} total / {scanDetail.snapshot?.skillPointsDebt} debt / {scanDetail.snapshot?.skillPointsEffective} effective</div>
                                                    <div><strong>Bank at scan:</strong> {scanDetail.snapshot?.bankBalance}</div>

                                                    <div className="cp-snapshot-subtitle">Skills</div>
                                                    <ul className="cp-snapshot-list">
                                                        {(scanDetail.snapshot?.skills || []).map((sk) => (
                                                            <li key={sk.skillId}>{sk.name || `#${sk.skillId}`} {sk.ranks ? `(x${sk.ranks})` : ''}</li>
                                                        ))}
                                                    </ul>

                                                    {(scanDetail.snapshot?.knowledges || []).length > 0 && (
                                                        <>
                                                            <div className="cp-snapshot-subtitle">Knowledges</div>
                                                            <ul className="cp-snapshot-list">
                                                                {scanDetail.snapshot.knowledges.map((k) => (
                                                                    <li key={k.knowledgeId}>{k.name || `#${k.knowledgeId}`} (x{k.ranks})</li>
                                                                ))}
                                                            </ul>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="em-empty cp-no-scan">No character sheet scan submitted for this attendee yet.</div>
                                        )}

                                        <div className="cp-checkout-form">
                                            <div className="cp-checkout-form-header">
                                                <div className="cp-checkout-form-title">Checkout Details</div>
                                                <button
                                                    type="button"
                                                    className={`em-btn em-btn-small ${selectedAttendee.checkoutProcessed ? 'em-btn-secondary' : 'em-btn-accent'}`}
                                                    onClick={handleToggleFinalize}
                                                    disabled={finalizing}
                                                >
                                                    {finalizing ? 'Saving…' : (selectedAttendee.checkoutProcessed ? 'Un-finalize' : 'Finalize')}
                                                </button>
                                            </div>
                                            <AttendanceRow
                                                key={selectedPlayerId}
                                                mode="edit"
                                                attendee={selectedAttendee}
                                                players={players}
                                                attendeeTypes={ATTENDEE_TYPES}
                                                eventDays={eventDays}
                                                eventStartDate={event.startDate}
                                                eventId={event.eventId}
                                                addedPlayerIds={new Set()}
                                                onSave={handleSaveAttendee}
                                                token={token}
                                                disabled={!!selectedAttendee.checkoutProcessed}
                                                startExpanded
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {showFullImage && (
                <div className="em-modal-overlay" onClick={() => setShowFullImage(false)}>
                    <div className="cp-image-modal" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="cp-image-modal-close" onClick={() => setShowFullImage(false)}>✕</button>
                        {loadingFullImage && <div className="em-loading">Loading full image…</div>}
                        {fullImageUrl && !loadingFullImage && (
                            <img src={fullImageUrl} alt="Full size scanned character sheet" className="cp-image-modal-img" />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CheckoutProcessor;
